import { useFlag } from '@unleash/nextjs';
import { useClickAway } from 'ahooks';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { sidebarAtom } from '@/common/atoms/sidebarAtom';
import InitialAccessConfirmationModal from '@/common/components/PlatformSwitcher/Modal/InitialAccessConfirmation';
import RequestHRISAccessModal from '@/common/components/PlatformSwitcher/Modal/RequestHRISAccessModal';
import { DashboardSidebar } from '@/common/components/Sidebar';
import { MOBILE_VIEW_WIDTH, UNLEASH_TOGGLES } from '@/common/constants';
import { useSidebar } from '@/common/contexts/useSidebar';
import useWindowSize from '@/common/hooks/useWindowSize';
import { cn } from '@/common/utils';
import ArrowCircleIcon from '@/icons/menu/ArrowCircleIcon';
import useAnalyticsBadge from '@/modules/analytics/hooks/useAnalyticsBadge';
import RequestPermissionModal from '@/modules/permissions/components/Departments/Modal/RequestPermission';
import useGeneralPermissionModal from '@/modules/permissions/hooks/useGeneralPermissionModal';
import PlatformSwitcher from './PlatformSwitcher';
import SidebarRoutes from './SidebarRoutes';

type SidebarProps = {
  hide: boolean;
};

const SidebarV2 = ({ hide }: SidebarProps) => {
  const router = useRouter();
  const {
    collapsed,
    mobileCollapsed,
    setCollapsed,
    setMobileCollapsed,
    setLoading,
  } = useSidebar();

  const { collapseButtonColor } = useAtomValue(sidebarAtom);

  const [width] = useWindowSize();
  const isMobile = width < MOBILE_VIEW_WIDTH;

  useEffect(() => {
    if (isMobile) setCollapsed(false);
    if (!isMobile) setMobileCollapsed(true);
  }, [width]);

  const ref = useRef<HTMLElement>(null);

  useClickAway((event) => {
    const isSidebarToggleClicked = (
      event?.target as HTMLElement
    )?.classList.contains('sidebar-toggle');

    const isHamburgerMenuClicked =
      (event?.target as HTMLElement)?.closest('.hamburger-menu') !== null;

    if (mobileCollapsed || !isMobile || isHamburgerMenuClicked) {
      return;
    }

    if (!isSidebarToggleClicked) {
      setMobileCollapsed(true);
    }
  }, ref);

  const HRIS_FEATURE_ENABLED = useFlag(UNLEASH_TOGGLES.ed_hrisFeature);
  const { setReleaseDate: setAnalyticsReleaseDate } = useAnalyticsBadge();

  const {
    permissionPayload,
    permissionResource,
    setRequestPermissionModalVisible,
    requestPermissionModalVisible,
  } = useGeneralPermissionModal();

  const [showRequestHRISAccessModal, setShowRequestHRISAccessModal] =
    useState(false);
  const [showSuperAdminConfirmationModal, setShowSuperAdminConfirmationModal] =
    useState(false);

  // Note: only used for test purposes, will be deleted after the Jira task is done
  useEffect(() => {
    if (router?.isReady) {
      const analyticsReleaseDate = router?.query?.['analytics-release-date'];

      if (analyticsReleaseDate) {
        setAnalyticsReleaseDate(analyticsReleaseDate as string);
      }
    }
  }, [router]);

  const isSidebarExpanded = isMobile ? !mobileCollapsed : !collapsed;

  if (hide) {
    return null;
  }

  return (
    <>
      <RequestPermissionModal
        isVisible={requestPermissionModalVisible}
        setIsVisible={setRequestPermissionModalVisible}
        requestedPermission={permissionPayload}
        resource={permissionResource}
      />

      {HRIS_FEATURE_ENABLED ? (
        <RequestHRISAccessModal
          opened={showRequestHRISAccessModal}
          onCancel={() => {
            setShowRequestHRISAccessModal(false);
            setLoading(false);
          }}
        />
      ) : null}

      {HRIS_FEATURE_ENABLED ? (
        <InitialAccessConfirmationModal
          opened={showSuperAdminConfirmationModal}
          onCancel={() => {
            setShowSuperAdminConfirmationModal(false);
            setLoading(false);
          }}
        />
      ) : null}

      <DashboardSidebar.Root
        value={{
          sidebarExpanded: isSidebarExpanded,
          isMobileSize: isMobile,
        }}
        className="z-[1000]"
      >
        <DashboardSidebar.Trigger
          onClick={() => {
            if (isMobile) {
              setMobileCollapsed(!mobileCollapsed);
            } else {
              setCollapsed(!collapsed);
            }
          }}
        >
          <ArrowCircleIcon
            variant={collapseButtonColor}
            style={
              collapseButtonColor === 'white'
                ? {
                    borderRadius: '50%',
                    boxShadow:
                      '0px 2px 12px rgba(0, 0, 0, 0.08), 0px 1px 4px rgba(0, 0, 0, 0.08)',
                  }
                : undefined
            }
            className={cn(
              'transition-all duration-500',
              !isSidebarExpanded ? 'rotate-180' : '',
            )}
          />
        </DashboardSidebar.Trigger>

        <DashboardSidebar.Container ref={ref}>
          <DashboardSidebar.Content>
            {HRIS_FEATURE_ENABLED && (
              <PlatformSwitcher
                setRequestCompanyModal={setShowRequestHRISAccessModal}
                setSuperAdminConfirmationModal={
                  setShowSuperAdminConfirmationModal
                }
              />
            )}
            <DashboardSidebar.Brand />
            <SidebarRoutes />
          </DashboardSidebar.Content>
        </DashboardSidebar.Container>
        <DashboardSidebar.Backdrop />
      </DashboardSidebar.Root>
    </>
  );
};

export default SidebarV2;
